import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Blocks, Plus, Trash2, ChevronRight, ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

type FieldType =
  | 'text'
  | 'rich_text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'image'
  | 'select'
  | 'color'
  | 'array'
  | 'collection_select'
  | 'field_select';

interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: string[];
  itemType?: string;
  collectionKey?: string;
}

interface BlockDef {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  fieldsJson: FieldDefinition[];
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'rich_text', label: 'Rich Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'image', label: 'Image URL' },
  { value: 'select', label: 'Select' },
  { value: 'color', label: 'Color' },
  { value: 'array', label: 'Array / Repeater' },
  { value: 'collection_select', label: 'Collection Select' },
  { value: 'field_select', label: 'Field Select (from collection)' },
];

const ARRAY_ITEM_TYPES: { value: string; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'rich_text', label: 'Rich Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'image', label: 'Image URL' },
  { value: 'select', label: 'Select' },
  { value: 'color', label: 'Color' },
];

const CATEGORIES = ['layout', 'content', 'media', 'commerce', 'loyalty', 'utility'];

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Field editor ──────────────────────────────────────────────────────────────

function BlockFieldEditor({
  fields,
  onChange,
}: {
  fields: FieldDefinition[];
  onChange: (fields: FieldDefinition[]) => void;
}) {
  function addField() {
    onChange([...fields, { key: '', label: '', type: 'text', required: false }]);
  }
  function removeField(i: number) {
    onChange(fields.filter((_, idx) => idx !== i));
  }
  function updateField(i: number, patch: Partial<FieldDefinition>) {
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }

  const inputCls =
    'border border-zinc-200 rounded-md text-sm px-2.5 h-8 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white w-full';

  return (
    <div className="space-y-2">
      {fields.length === 0 && <p className="text-xs text-zinc-400">No fields defined yet.</p>}
      {fields.map((f, i) => (
        <div key={i} className="border border-zinc-100 rounded-lg p-3 space-y-2 bg-zinc-50">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Key</label>
              <input
                className={inputCls}
                placeholder="e.g. title"
                value={f.key}
                onChange={(e) =>
                  updateField(i, { key: e.target.value.replace(/\s/g, '_').toLowerCase() })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Label</label>
              <input
                className={inputCls}
                placeholder="e.g. Title"
                value={f.label}
                onChange={(e) => updateField(i, { label: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Type</label>
              <select
                className={inputCls}
                value={f.type}
                onChange={(e) =>
                  updateField(i, {
                    type: e.target.value as FieldType,
                    options: undefined,
                    itemType: undefined,
                    collectionKey: undefined,
                  })
                }
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {f.type === 'select' && (
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Options (comma-separated)
              </label>
              <input
                className={inputCls}
                placeholder="Option A, Option B, Option C"
                value={(f.options ?? []).join(', ')}
                onChange={(e) =>
                  updateField(i, {
                    options: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
          )}

          {f.type === 'field_select' && (
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Collection Key (key of the sibling collection_select field)
              </label>
              <input
                className={inputCls}
                placeholder="e.g. typeSlug"
                value={f.collectionKey ?? ''}
                onChange={(e) => updateField(i, { collectionKey: e.target.value || undefined })}
              />
            </div>
          )}

          {f.type === 'array' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Item Type</label>
                <select
                  className={inputCls}
                  value={f.itemType ?? 'text'}
                  onChange={(e) =>
                    updateField(i, {
                      itemType: e.target.value,
                      options: e.target.value !== 'select' ? undefined : f.options,
                    })
                  }
                >
                  {ARRAY_ITEM_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              {f.itemType === 'select' && (
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Options (comma-separated)
                  </label>
                  <input
                    className={inputCls}
                    placeholder="A, B, C"
                    value={(f.options ?? []).join(', ')}
                    onChange={(e) =>
                      updateField(i, {
                        options: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={!!f.required}
                onChange={(e) => updateField(i, { required: e.target.checked })}
                className="rounded border-zinc-300"
              />
              Required
            </label>
            <button
              type="button"
              onClick={() => removeField(i)}
              className="text-xs text-red-500 hover:text-red-700 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addField}
        className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 transition-colors pt-1"
      >
        <Plus size={12} />
        Add field
      </button>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsBlockDefsPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<BlockDef | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BlockDef | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [icon, setIcon] = useState('');
  const [fields, setFields] = useState<FieldDefinition[]>([]);

  const { data: blockDefs = [], isLoading } = useQuery<BlockDef[]>({
    queryKey: ['cms-block-defs'],
    queryFn: () => api.get('/api/v1/cms/block-definitions').then((r) => r.data),
  });

  function openCreate() {
    setName('');
    setDescription('');
    setCategory('');
    setIcon('');
    setFields([]);
    setEditTarget(null);
    setShowCreate(true);
  }

  function openEdit(bd: BlockDef) {
    setName(bd.name);
    setDescription(bd.description ?? '');
    setCategory(bd.category ?? '');
    setIcon(bd.icon ?? '');
    setFields(bd.fieldsJson ?? []);
    setEditTarget(bd);
    setShowCreate(true);
  }

  const createMutation = useMutation({
    mutationFn: (body: {
      name: string;
      description: string;
      category: string;
      icon: string;
      fieldsJson: FieldDefinition[];
    }) => api.post('/api/v1/cms/block-definitions', body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-block-defs'] });
      toast.success('Block definition created');
      setShowCreate(false);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        name: string;
        description: string;
        category: string;
        icon: string;
        fieldsJson: FieldDefinition[];
      };
    }) => api.patch(`/api/v1/cms/block-definitions/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-block-defs'] });
      toast.success('Block definition updated');
      setShowCreate(false);
      setEditTarget(null);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/cms/block-definitions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-block-defs'] });
      toast.success('Block definition deleted');
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/v1/cms/block-definitions/${id}`, { isActive }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cms-block-defs'] }),
    onError: (err) => toast.error(extractError(err)),
  });

  function handleSave() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body = {
      name: name.trim(),
      description: description.trim(),
      category: category.trim(),
      icon: icon.trim(),
      fieldsJson: fields,
    };
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, body });
    } else {
      createMutation.mutate(body);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Block Definitions"
        description="Define reusable block schemas that can be embedded in CMS pages."
        action={
          <Button onClick={openCreate} size="sm">
            <Plus size={14} className="mr-1" />
            New Block Definition
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <tbody>
              <SkeletonRows count={3} cols={5} />
            </tbody>
          </table>
        </div>
      ) : blockDefs.length === 0 ? (
        <EmptyState
          icon={<Blocks size={24} />}
          title="No block definitions yet"
          description="Create reusable block schemas to use in your CMS pages."
          action={{ label: 'New Block Definition', onClick: openCreate }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Name
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Slug
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Category
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Fields
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Status
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Updated
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {blockDefs.map((bd) => (
                <tr key={bd.id} className="hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{bd.name}</p>
                    {bd.description && (
                      <p className="text-xs text-zinc-400 mt-0.5 truncate max-w-xs">
                        {bd.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{bd.slug}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {bd.category ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-zinc-100 text-zinc-600 text-[10px] font-medium uppercase tracking-wide">
                        {bd.category}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500">{bd.fieldsJson.length} fields</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={bd.isActive ? 'success' : 'default'}>
                        {bd.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {bd.isSystem && <Badge variant="info">System</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmt(bd.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {!bd.isSystem && (
                        <button
                          title={bd.isActive ? 'Deactivate' : 'Activate'}
                          onClick={() =>
                            toggleMutation.mutate({ id: bd.id, isActive: !bd.isActive })
                          }
                          className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
                        >
                          {bd.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                        </button>
                      )}
                      <button
                        title="Edit"
                        onClick={() => openEdit(bd)}
                        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        <ChevronRight size={15} />
                      </button>
                      {!bd.isSystem && (
                        <button
                          title="Delete"
                          onClick={() => setDeleteTarget(bd)}
                          className="p-1.5 rounded hover:bg-red-50 text-zinc-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal
        open={showCreate}
        title={editTarget ? `Edit: ${editTarget.name}` : 'New Block Definition'}
        onClose={() => {
          setShowCreate(false);
          setEditTarget(null);
        }}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Hero Banner"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">— None —</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </Select>
            <Input
              label="Icon (emoji or name)"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="e.g. 🦸 or hero"
            />
          </div>
          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={2}
          />
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">Fields</label>
            <BlockFieldEditor fields={fields} onChange={setFields} />
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setShowCreate(false);
              setEditTarget(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : editTarget ? 'Save changes' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Block Definition"
        message={
          deleteTarget
            ? `"${deleteTarget.name}" will be permanently deleted. Pages using this block may be affected.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
